import { searchApiClient } from '@/datasources/search.datasource';
import {
  SearchApiBranch,
  SearchApiDayTime,
  SearchApiFacility,
  SearchApiFacilityExam,
  SearchApiItemResponse,
  SearchApiListResponse,
  SearchApiPartner,
  SearchApiPartnerProcedureGroup,
  SearchApiProcedure,
  SearchApiProcedureGroup,
  SearchApiProcedurePricing,
  SearchApiProfessional,
  SearchApiRating,
  SearchApiRatingComment,
  SearchApiRatingCommentsOrderBy,
  SearchApiScheduleSlotGroup,
  SearchApiServiceChannel,
} from '@/app/(main)/(pages)/_types/search-api-response.type';

export async function findProceduresBySlugs(slugs: string[]): Promise<SearchApiProcedure[]> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiProcedure>>(
    '/procedures/find-by-slugs',
    { slugs },
  );

  return response.data.data ?? [];
}

export interface FindSchedulesByProfessionalIdParams {
  serviceChannel?: SearchApiServiceChannel;
  partnerFacilityId?: string;
  procedureIds?: string[];
  page?: number;
  limit?: number;
}

export interface FindSchedulesByProfessionalIdResult {
  groups: SearchApiProcedureGroup[];
  hasMore: boolean;
}

export async function findSchedulesByProfessionalId(
  id: string,
  params: FindSchedulesByProfessionalIdParams = {},
): Promise<FindSchedulesByProfessionalIdResult> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiProcedureGroup>>(
    `/schedules/find-by-professional-id/${id}`,
    { ...params },
  );

  return { groups: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export interface FindSchedulesByPartnerIdParams {
  serviceChannel?: SearchApiServiceChannel;
  partnerFacilityId?: string;
  procedureIds?: string[];
  page?: number;
  limit?: number;
}

export interface FindSchedulesByPartnerIdResult {
  groups: SearchApiPartnerProcedureGroup[];
  hasMore: boolean;
}

type RawPartnerProcedureGroup = Omit<SearchApiPartnerProcedureGroup, 'procedure'> & {
  procedure?: Partial<SearchApiPartnerProcedureGroup['procedure']>;
};

function toPartnerProcedureGroup(
  group: RawPartnerProcedureGroup,
): SearchApiPartnerProcedureGroup | null {
  const { id, name, slug } = group.procedure ?? {};

  if (!id || !slug) {
    return null;
  }

  return { ...group, procedure: { id, name: name ?? '', slug } };
}

export async function findSchedulesByPartnerId(
  id: string,
  params: FindSchedulesByPartnerIdParams = {},
): Promise<FindSchedulesByPartnerIdResult> {
  const response = await searchApiClient.get<SearchApiListResponse<RawPartnerProcedureGroup>>(
    `/schedules/find-by-partner-id/${id}`,
    { ...params },
  );

  return {
    groups: (response.data.data ?? [])
      .map(toPartnerProcedureGroup)
      .filter((group): group is SearchApiPartnerProcedureGroup => group !== null),
    hasMore: response.data.hasMore ?? false,
  };
}

export interface FindSlotsByScheduleIdsParams {
  limitDays: number;
  offsetDays: number;
  dayTime?: SearchApiDayTime;
  minDate?: string;
  maxDate?: string;
}

export interface FindSlotsByScheduleIdsResult {
  groups: SearchApiScheduleSlotGroup[];
  hasMore: boolean;
}

export async function findSlotsByScheduleIds(
  scheduleIds: string[],
  params: FindSlotsByScheduleIdsParams,
): Promise<FindSlotsByScheduleIdsResult> {
  const { dayTime, minDate, maxDate, ...rest } = params;

  const response = await searchApiClient.get<SearchApiListResponse<SearchApiScheduleSlotGroup>>(
    '/schedules/find-slots-by-schedule-ids',
    {
      scheduleIds,
      ...rest,
      ...(dayTime ? { dayTime } : {}),
      ...(minDate ? { minDate } : {}),
      ...(maxDate ? { maxDate } : {}),
    },
  );

  return { groups: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export async function findProfessionalById(id: string): Promise<SearchApiProfessional> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiProfessional>>(
    `/professionals/find-by-id/${id}`,
  );

  return response.data.data;
}

export async function findProfessionalBySlug(slug: string): Promise<SearchApiProfessional> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiProfessional>>(
    `/professionals/find-by-slug/${slug}`,
  );

  return response.data.data;
}

export async function findProfessionalRatingById(id: string): Promise<SearchApiRating> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiRating>>(
    `/professionals/find-rating-by-id/${id}`,
  );

  return response.data.data;
}

export interface FindProfessionalRatingCommentsByIdParams {
  page?: number;
  limit?: number;
  orderBy?: SearchApiRatingCommentsOrderBy;
}

export interface FindProfessionalRatingCommentsByIdResult {
  comments: SearchApiRatingComment[];
  hasMore: boolean;
}

export async function findProfessionalRatingCommentsById(
  id: string,
  params: FindProfessionalRatingCommentsByIdParams = {},
): Promise<FindProfessionalRatingCommentsByIdResult> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiRatingComment>>(
    `/professionals/find-rating-comments-by-id/${id}`,
    { ...params },
  );

  return { comments: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export async function findPartnerById(id: string): Promise<SearchApiPartner> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiPartner>>(
    `/partners/find-by-id/${id}`,
  );

  return response.data.data;
}

export async function findFacilityById(id: string): Promise<SearchApiFacility> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiFacility>>(
    `/partners/facilities/find-by-id/${id}`,
  );

  return response.data.data;
}

export async function findFacilityBySlug(slug: string): Promise<SearchApiFacility> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiFacility>>(
    `/partners/facilities/find-by-slug/${slug}`,
  );

  return response.data.data;
}

export async function findFacilityRatingById(id: string): Promise<SearchApiRating> {
  const response = await searchApiClient.get<SearchApiItemResponse<SearchApiRating>>(
    `/partners/facilities/find-rating-by-id/${id}`,
  );

  return response.data.data;
}

export interface FindFacilityRatingCommentsByIdParams {
  page?: number;
  limit?: number;
  orderBy?: SearchApiRatingCommentsOrderBy;
}

export interface FindFacilityRatingCommentsByIdResult {
  comments: SearchApiRatingComment[];
  hasMore: boolean;
}

export async function findFacilityRatingCommentsById(
  id: string,
  params: FindFacilityRatingCommentsByIdParams = {},
): Promise<FindFacilityRatingCommentsByIdResult> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiRatingComment>>(
    `/partners/facilities/find-rating-comments-by-id/${id}`,
    { ...params },
  );

  return { comments: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export interface FindFacilityExamsByIdParams {
  term?: string;
  page?: number;
  limit?: number;
}

export interface FindFacilityExamsByIdResult {
  exams: SearchApiFacilityExam[];
  hasMore: boolean;
}

export async function findFacilityExamsById(
  id: string,
  params: FindFacilityExamsByIdParams = {},
): Promise<FindFacilityExamsByIdResult> {
  const { term, ...rest } = params;

  const response = await searchApiClient.get<SearchApiListResponse<SearchApiFacilityExam>>(
    `/partners/facilities/find-exams-by-id/${id}`,
    { ...rest, ...(term ? { term } : {}) },
  );

  return { exams: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export interface FindBranchesByLocationParams {
  city?: string;
  latitude?: number;
  longitude?: number;
  page?: number;
  limit?: number;
}

export interface FindBranchesByLocationResult {
  branches: SearchApiBranch[];
  hasMore: boolean;
}

export async function findBranchesByLocation(
  params: FindBranchesByLocationParams,
): Promise<FindBranchesByLocationResult> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiBranch>>(
    '/branches/find-by-location',
    { ...params },
  );

  return { branches: response.data.data ?? [], hasMore: response.data.hasMore ?? false };
}

export async function findProceduresPricing(
  partnerFacilityId: string,
  procedureIds: string[],
): Promise<SearchApiProcedurePricing | null> {
  const response = await searchApiClient.get<SearchApiListResponse<SearchApiProcedurePricing>>(
    '/partners/facilities/find-procedures-pricing',
    { partnerFacilityIds: partnerFacilityId, procedureIds },
  );

  return response.data.data?.[0] ?? null;
}
